import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import {
  AlertTriangleIcon,
  CalendarCheckIcon,
  FileTextIcon,
  InboxIcon,
  PackageCheckIcon,
  TimerIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { deliverablesApi } from '@/api/deliverables'
import { ApiError } from '@/api/errors'
import { queryKeys } from '@/api/keys'
import { quotesApi } from '@/api/quotes'
import { dashboardApi } from '@/api/search'
import type { TaskSummary } from '@/api/types'
import { useCurrentUser } from '@/auth/use-auth'
import { ClientLink } from '@/components/client-link'
import { AllClientsLists } from '@/components/dashboard/all-clients-lists'
import { ClientFeedbackCard } from '@/components/dashboard/client-feedback-card'
import { OpenMeetingPollsCard } from '@/components/dashboard/open-meeting-polls-card'
import { OpenQuestionnairesCard } from '@/components/dashboard/open-questionnaires-card'
import { WeekProgramme } from '@/components/dashboard/week-programme'
import { EmptyState } from '@/components/empty-state'
import { PageHeader } from '@/components/page-header'
import { QuoteStatusBadge } from '@/components/quotes/quote-status-badge'
import { DormantClients } from '@/components/reminders/dormant-clients'
import { DueDate } from '@/components/tasks/due-date'
import { TaskPriorityIcon } from '@/components/tasks/task-badges'
import { DEFAULT_TASKS_SEARCH } from '@/components/tasks/tasks-search-schema'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrigin } from '@/hooks/use-return-to'
import { formatDateOnly, formatDuration } from '@/lib/format'
import { formatMoney } from '@/lib/money'
import { targetShortLabel } from '@/lib/time-entry'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/_app/')({
  component: DashboardPage,
})

const PENDING_REQUESTS_SEARCH = {
  ...DEFAULT_TASKS_SEARCH,
  demandes: true,
  statuts: 'backlog',
  assigne: 'none',
} as const

const SENT_QUOTES = { status: 'sent', sort: 'valid_until', page_size: 8 } as const
const AWAITING_DELIVERIES = { status: 'delivered', sort: 'due_date', page_size: 8 } as const

function greeting(hour = new Date().getHours()): string {
  return hour >= 5 && hour < 18 ? 'Bonjour' : 'Bonsoir'
}

/** Un chiffre qui mène quelque part : la tuile entière est le lien. */
function StatTile({
  icon: Icon,
  label,
  value,
  hint,
  to,
  params,
  search,
  tone = 'default',
  testId,
}: {
  icon: typeof InboxIcon
  label: string
  value: ReactNode
  hint?: ReactNode
  to: string
  params?: Record<string, string>
  search?: Record<string, unknown>
  tone?: 'default' | 'warning' | 'success'
  testId?: string
}) {
  return (
    <Card
      className={cn(
        'transition-colors hover:bg-accent/40',
        tone === 'warning' && 'border-destructive/40',
        tone === 'success' && 'border-success/40',
      )}
      data-testid={testId}
    >
      <Link
        to={to}
        params={params}
        search={search}
        className="block rounded-xl focus-visible:outline-2 focus-visible:outline-ring"
      >
        <CardContent className="space-y-1 pt-5">
          <p className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
            <Icon className="size-3.5" aria-hidden />
            {label}
          </p>
          <p className="text-2xl font-semibold tabular-nums">{value}</p>
          {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
        </CardContent>
      </Link>
    </Card>
  )
}

function TaskLine({ task, testId = 'dashboard-task' }: { task: TaskSummary; testId?: string }) {
  const origin = useOrigin()
  return (
    <li data-testid={testId} className="flex items-start gap-2 text-sm">
      <TaskPriorityIcon priority={task.priority} />
      <div className="min-w-0 flex-1">
        <Link
          to="/tasks/$taskKey"
          params={{ taskKey: task.key }}
          search={{ ...DEFAULT_TASKS_SEARCH, ...origin }}
          className="block truncate hover:underline"
        >
          <span className="font-key text-xs text-muted-foreground">{task.key}</span> {task.title}
        </Link>
        <div className="flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground">
          <ClientLink client={task.client} clientId={task.client.id}>
            {task.client.name}
          </ClientLink>
          {task.due_date && <DueDate dueDate={task.due_date} status={task.status} />}
        </div>
      </div>
    </li>
  )
}

/**
 * Le tableau de bord est un poste de pilotage (Marc, 2026-09-20) : le
 * programme de la semaine, les demandes des clients en tête, ce qui attend
 * une décision chez eux, le chrono et le temps — tout cliquable vers l'objet.
 */
function DashboardPage() {
  const origin = useOrigin()
  const user = useCurrentUser()
  const firstName = user.name.split(' ')[0] ?? user.name

  const dashboard = useQuery({ queryKey: queryKeys.dashboard, queryFn: () => dashboardApi.get() })
  const sentQuotes = useQuery({
    queryKey: queryKeys.quotes.list(SENT_QUOTES),
    queryFn: () => quotesApi.list(SENT_QUOTES),
  })
  const awaitingDeliveries = useQuery({
    queryKey: ['deliverables', 'awaiting', AWAITING_DELIVERIES] as const,
    queryFn: () => deliverablesApi.listAll(AWAITING_DELIVERIES),
  })

  const data = dashboard.data
  const quotesToDecide = sentQuotes.data?.items ?? []
  const deliveriesToAccept = awaitingDeliveries.data?.items ?? []

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={
          <span className="text-sm">
            {greeting()}, {firstName}.
          </span>
        }
        title="Ma semaine"
        documentTitle="Tableau de bord"
      />

      <AllClientsLists />

      {dashboard.isPending ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }, (_, index) => (
            <Skeleton key={index} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      ) : dashboard.isError ? (
        <EmptyState
          icon={AlertTriangleIcon}
          title="Impossible de charger le tableau de bord"
          description={
            dashboard.error instanceof ApiError
              ? dashboard.error.message
              : 'Réessayez dans un instant.'
          }
          action={
            <Button
              variant="outline"
              onClick={() => {
                void dashboard.refetch()
              }}
            >
              Réessayer
            </Button>
          }
        />
      ) : data ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <StatTile
              icon={InboxIcon}
              label="Demandes des clients"
              value={data.client_requests_count}
              hint={data.client_requests_count > 0 ? 'À prendre' : 'Rien à prendre'}
              to="/tasks"
              search={PENDING_REQUESTS_SEARCH}
              tone={data.client_requests_count > 0 ? 'warning' : 'default'}
              testId="tuile-demandes"
            />
            <StatTile
              icon={AlertTriangleIcon}
              label="En retard"
              value={data.overdue_count}
              hint={data.overdue_count > 0 ? 'Échéance dépassée' : 'Rien en retard'}
              to="/tasks"
              search={{ ...DEFAULT_TASKS_SEARCH, assigne: 'me', tri: 'due_date' }}
              tone={data.overdue_count > 0 ? 'warning' : 'success'}
            />
            <StatTile
              icon={CalendarCheckIcon}
              label="Échéances de la semaine"
              value={data.due_this_week_count}
              to="/tasks"
              search={{ ...DEFAULT_TASKS_SEARCH, assigne: 'me' }}
            />
            <StatTile
              icon={TimerIcon}
              label={data.running_timer ? 'Chrono en cours' : 'Temps aujourd’hui'}
              value={formatDuration(data.time_today_seconds)}
              hint={
                data.running_timer
                  ? `Chronomètre en cours sur ${targetShortLabel(data.running_timer)} · Ouvrir la tâche`
                  : `${formatDuration(data.time_week_seconds)} cette semaine`
              }
              to={data.running_timer?.task ? '/tasks/$taskKey' : '/time'}
              params={
                data.running_timer?.task ? { taskKey: data.running_timer.task.key } : undefined
              }
              search={data.running_timer?.task ? DEFAULT_TASKS_SEARCH : { tab: 'feuille' }}
              tone={data.running_timer ? 'success' : 'default'}
              testId="tuile-temps"
            />
          </div>

          <WeekProgramme />

          <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
            <div className="space-y-6">
              {data.client_requests.length > 0 && (
                <Card className="border-brand/40" data-testid="demandes-clients">
                  <CardHeader className="flex-row items-center justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <InboxIcon className="size-4" />
                      Demandes des clients
                    </CardTitle>
                    <Button asChild variant="link" size="sm" className="h-auto p-0">
                      <Link to="/tasks" search={PENDING_REQUESTS_SEARCH}>
                        Tout voir
                      </Link>
                    </Button>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <ul className="space-y-2">
                      {data.client_requests.map((task) => (
                        <TaskLine key={task.id} task={task} testId="dashboard-client-request" />
                      ))}
                    </ul>
                    {data.client_requests_count > data.client_requests.length && (
                      <Button asChild variant="link" size="sm" className="h-auto p-0">
                        <Link to="/tasks" search={PENDING_REQUESTS_SEARCH}>
                          Voir les {data.client_requests_count} demandes en attente
                        </Link>
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}

              <Card>
                <CardHeader className="flex-row items-center justify-between">
                  <CardTitle>Mes tâches ouvertes</CardTitle>
                  <Button asChild variant="link" size="sm" className="h-auto p-0">
                    <Link to="/tasks" search={{ ...DEFAULT_TASKS_SEARCH, assigne: 'me' }}>
                      Tout voir
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  {data.my_open_tasks.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucune tâche ouverte</p>
                  ) : (
                    <ul className="space-y-2">
                      {data.my_open_tasks.slice(0, 8).map((task) => (
                        <TaskLine key={task.id} task={task} />
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <Card data-testid="a-decider-chez-le-client">
                <CardHeader className="flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <PackageCheckIcon className="size-4" />
                    Chez le client
                  </CardTitle>
                  <Button asChild variant="link" size="sm" className="h-auto p-0">
                    <Link to="/quotes" search={{ statut: 'sent', page: 1 }}>
                      Soumissions envoyées
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent className="space-y-4 text-sm">
                  <section className="space-y-1.5">
                    <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <FileTextIcon className="size-3.5" aria-hidden />
                      Soumissions à décider
                    </h3>
                    {sentQuotes.isPending ? (
                      <Skeleton className="h-8 w-full" />
                    ) : quotesToDecide.length === 0 ? (
                      <p className="text-muted-foreground">Aucune.</p>
                    ) : (
                      <ul className="space-y-1">
                        {quotesToDecide.map((quote) => (
                          <li
                            key={quote.id}
                            className="flex min-w-0 items-center gap-2"
                            data-testid="dashboard-soumission"
                          >
                            <Link
                              to="/quotes/$quoteNumber"
                              params={{ quoteNumber: quote.number }}
                              search={origin}
                              className="font-key text-xs hover:underline"
                            >
                              {quote.number}
                            </Link>
                            <span className="min-w-0 flex-1 truncate">
                              {quote.client.name} · {quote.title}
                            </span>
                            <span className="tabular-nums">{formatMoney(quote.total_cents)}</span>
                            {quote.valid_until && (
                              <span className="text-xs text-muted-foreground">
                                {formatDateOnly(quote.valid_until)}
                              </span>
                            )}
                            <QuoteStatusBadge status={quote.status} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                  <section className="space-y-1.5">
                    <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
                      <PackageCheckIcon className="size-3.5" aria-hidden />
                      Livraisons à recetter
                    </h3>
                    {awaitingDeliveries.isPending ? (
                      <Skeleton className="h-8 w-full" />
                    ) : awaitingDeliveries.isError ? (
                      <p className="text-muted-foreground">Indisponible.</p>
                    ) : deliveriesToAccept.length === 0 ? (
                      <p className="text-muted-foreground">Aucune.</p>
                    ) : (
                      <ul className="space-y-1">
                        {deliveriesToAccept.map((deliverable) => (
                          <li
                            key={deliverable.id}
                            className="flex min-w-0 items-center gap-2"
                            data-testid="dashboard-livraison"
                          >
                            <Link
                              to="/projects/$projectRef/livrables/$key"
                              params={{ projectRef: deliverable.project.ref, key: deliverable.key }}
                              search={origin}
                              className="font-key text-xs hover:underline"
                            >
                              {deliverable.key}
                            </Link>
                            <span className="min-w-0 flex-1 truncate">{deliverable.title}</span>
                            <span className="text-xs text-muted-foreground">
                              {deliverable.project.ref}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </section>
                </CardContent>
              </Card>

              <OpenMeetingPollsCard />

              <OpenQuestionnairesCard />

              <ClientFeedbackCard />

              <Card>
                <CardHeader className="flex-row items-center justify-between">
                  <CardTitle>Clients à rappeler</CardTitle>
                  <Button asChild variant="link" size="sm" className="h-auto p-0">
                    <Link to="/relances" search={{ onglet: 'dormants' }}>
                      Tout voir
                    </Link>
                  </Button>
                </CardHeader>
                <CardContent>
                  <DormantClients limit={5} />
                </CardContent>
              </Card>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
